// import Counter from './Counter.js';
import { HeroSearchForm } from "./HeroForm.js";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Paws, Title } from "../../../shared/svgs/Backgrounds.js";
import { renderToStaticMarkup } from "react-dom/server";
import { IS_NATIVE_APP } from "../../../config/appTarget.js";
import { PAGE_GUTTER_CLASS } from "../../../shared/PageContainer.js";
import AnunciarFloater from "./AnunciarFloater.js";
import TopFloaters from "./TopFloaters.js";

const HeroSection = () => {
  const { t } = useTranslation("landing");
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedPet, setSelectedPet] = useState<"cao" | "gato" | null>(null);

  useEffect(() => {
    setIsLoaded(true);
    // Preload the LCP image (dog frame)
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = "/assets/MolduraCao-original.webp";
    document.head.appendChild(link);

    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  return (
    <section
      className="relative min-h-screen overflow-hidden flex md:pt-[4vh] justify-center z-10"
      style={
        IS_NATIVE_APP
          ? { paddingTop: "calc(env(safe-area-inset-top) + 16px)" }
          : { paddingTop: "8vh" }
      }
      // style={{ backgroundImage: {newHeroBackground} }}
    >
      {/* Decorative repeated christmas lights across the width */}
      {/* <div
        aria-hidden
        className="absolute top-0  left-1 md:left-5 w-[20%] md:w-[10%] z-30 pointer-events-none bg-no-repeat h-[120px] md:h-[250px]"
        style={{
          backgroundImage: `url(${star})`,
          backgroundSize: `150px auto`,
          backgroundPosition: `left 100%`,
        }}
      /> */}
      {/* <Snowfall
        enable3DRotation
        opacity={[0.5, 0.2]}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh", // Restrict to first view height
          zIndex: 20,
        }}
      /> */}
      {/* Overlay */}
      <div
        className="absolute inset-0 flex repeat-infinite justify-center bg-hero-gradient pointer-events-none z-0"
        style={{
          clipPath: "url(#s-curve)",
        }}
      >
        <svg width="0" height="0" style={{ position: "absolute" }}>
          <defs>
            <clipPath id="s-curve" clipPathUnits="objectBoundingBox">
              <path d="M 0,0 L 1,0 L 1,2.7 C 0.8,2.8 0.6,2.6 0.5,2.65 C 0.4,2.7 0.2,2.6 0,2.7 Z" />
            </clipPath>
          </defs>
        </svg>
        {/* Decorative paws tiled/stacked — use the existing Paws SVG as a repeating background */}
        {/* We render the Paws component to a static SVG string and use it as a data URL so it can repeat-y */}
        <style>{`
          .hero-paws-inline{background-repeat-y:repeat;background-position:center top;background-size:850px; top:-100px; }
          @media (min-width:640px){.hero-paws-inline{background-size:850px }}
          @media (min-width:768px){.hero-paws-inline{background-size:1200px }}
          @media (min-width:1024px){.hero-paws-inline{background-size:1600px }}
          @media (min-width:1280px){.hero-paws-inline{background-size:1500px }}
          /* Subtle shake animation when a pet is selected */
            /* Subtle shake animation when a pet is selected.
               Animation duration is long so the actual shake happens
               briefly once every few seconds. */
            @keyframes shake {
              0% { transform: rotate(0deg); }
              80% { transform: rotate(0deg); }
              85% { transform: rotate(-3deg); }
              90% { transform: rotate(3deg); }
              95% { transform: rotate(-2deg); }
              100% { transform: rotate(0deg); }
            }
            /* 3.5s cycle: a short shake near the end, then rest */
            .animate-shake { animation: shake 3.5s ease-in-out infinite; }
        `}</style>

        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none z-0 hero-paws-inline"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
              renderToStaticMarkup(<Paws />),
            )}")`,
          }}
        />

      </div>

      {/* Interactive image frames — lifted out of the decorative overlay so
          they receive clicks, and aligned to the shared page gutter. The
          wrapper is click-through; only the frame buttons capture events. */}
      <div className="absolute inset-0 z-[5] pointer-events-none">
        <div className={`relative h-full ${PAGE_GUTTER_CLASS}`}>
          {/* Dog frame: right edge aligned to the page's right gutter. */}
          <span
            className={`absolute overflow-hidden z-20
              right-0 top-[15vh]
              sm:top-[16vh]
              translate-x-[8%]
              md:top-[10vh]  lg:top-[-7vh]
              lg:bottom-[3vh]
              max-w-[35vw]
              lg:w-[50.4%]
              md:w-[35vh]
              sm:w-[50vh]
              w-[35vw]
            `}
          >
            <button
              type="button"
              aria-pressed={selectedPet === "cao"}
              aria-label={t("hero.selectDogs")}
              onClick={() => setSelectedPet("cao")}
              className={`w-full h-full block overflow-hidden cursor-pointer pointer-events-auto focus:outline-none focus:ring-2 focus:ring-gradient-blue rounded-lg ${
                selectedPet === "cao" ? "ring-4 ring-gradient-blue" : ""
              }`}
            >
              <img
                src="/assets/MolduraCao-original.webp"
                alt={t("hero.dogFrameAlt")}
                width="800"
                height="800"
                className={`w-full h-full object-contain transition-transform duration-300 transform ${
                  selectedPet === "cao"
                    ? "animate-shake"
                    : "hover:-rotate-3 hover:scale-105"
                }`}
                loading="eager"
              />
            </button>
          </span>

          {/* Cat frame: hidden on mobile; on desktop sits to the left of the
              dog, offset proportionally to the container width. */}
          <span
            className={`hidden md:block absolute overflow-hidden z-30
              md:top-[10%] md:right-[31%]
              lg:top-[5%] lg:right-[30%]
              max-w-[36vw]
              lg:w-[24vw]
              md:w-[25vh]
            `}
          >
            <button
              type="button"
              aria-pressed={selectedPet === "gato"}
              aria-label={t("hero.selectCats")}
              onClick={() => setSelectedPet("gato")}
              className={`w-full h-full block overflow-hidden  cursor-pointer pointer-events-auto focus:outline-none focus:ring-2 focus:ring-gradient-blue rounded-lg ${
                selectedPet === "gato" ? "ring-4 ring-gradient-blue" : ""
              }`}
            >
              <img
                src="/assets/MolduraGato-original.webp"
                alt={t("hero.catFrameAlt")}
                width="600"
                height="600"
                className={`w-full h-full object-contain transition-transform duration-300 transform ${
                  selectedPet === "gato"
                    ? "animate-shake"
                    : "hover:rotate-3 hover:scale-105"
                }`}
                loading="lazy"
              />
            </button>
          </span>
        </div>
      </div>

      {/* Gradient definition */}

      {/* Content — matches the shared page gutter from sm up, but keeps more
          horizontal padding on mobile so the title + form aren't edge-to-edge. */}
      <div className="mx-auto w-full max-w-7xl px-8 sm:px-6 lg:px-10 xl:px-16 relative z-10 pointer-events-none">
        <div
          className={`text-start max-w-2xl md:mt-20 ${IS_NATIVE_APP ? "mt-32" : "mt-10"} transition-all duration-1000 ${
            isLoaded ? "animate-fadeInUp opacity-100" : "opacity-0"
          }`}
        >
          <div
            className={`w-[30vh] md:-translate-x-4 justify-center sm:w-[30vh] md:w-[40vh] lg:w-[50vh] xl:w-[50vh]${IS_NATIVE_APP ? " mt-8" : ""}`}
          >
            <Title />
          </div>

          {/* Search Form */}
          <div className="pointer-events-auto">
            <HeroSearchForm
              selectedPet={selectedPet}
              setSelectedPet={setSelectedPet}
            />
          </div>
          {/* <Counter /> */}
        </div>
      </div>

      <AnunciarFloater />
      <TopFloaters />
    </section>
  );
};

export default HeroSection;
